"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box, Button, ButtonGroup, Radio } from "@mui/material"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { useId } from "react"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { TimeFrameSelectorProps, TimeFrameType } from "./types"

const StyledButtonGroup = styled(ButtonGroup)`
  border-radius: 8px;
  width: fit-content;
  gap: ${sizes.baseSingle};
  .MuiButtonGroup-grouped {
    margin-left: 0;
    margin-right: 0;
    border-radius: 50px;
    border-left: 1px solid ${colors.red};
    border-right: 1px solid ${colors.red};
  }
  ${mediaQueries.desktopM} {
    gap: 20px;
  }
`

const StyledResetButton = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  a {
    color: ${colors.black};
  }
`

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>`
  padding: 5px 10px;
  border: 1px solid ${colors.red};
  border-radius: 50px;
  color: ${(props) => (props.selected ? colors.white : colors.black)};
  background-color: ${(props) => (props.selected ? colors.red : colors.white)};
  font-size: 12px;
  &:hover {
    background-color: ${(props) =>
      props.selected ? colors.red : colors.beige};
    border-color: ${colors.red};
  }

  /* Hide actual radio input but keep it accessible */
  .MuiRadio-root {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
  ${mediaQueries.desktopM} {
    font-size: 16px;
    padding: 6px 16px;
  }
`

const StyledContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 16px;
`

const TimeFrameSelector = ({
  timeFrame,
  onChange,
  resetSimulation,
}: TimeFrameSelectorProps) => {
  const baseId = useId()
  const t = useTranslations("InvestSimulator.timeFrame")

  const timeFrameOptions: Array<{ value: TimeFrameType; label: string }> = [
    { value: "12m", label: t("12months") },
    { value: "3y", label: t("3years") },
    { value: "5y", label: t("5years") },
  ]

  const handleChange = (value: TimeFrameType) => {
    onChange(value)
  }

  const CircleOfArrowsIcon = iconsMap.circleOfArrows

  return (
    <StyledContainer>
      <StyledButtonGroup variant="outlined" aria-label={t("selector")}>
        {timeFrameOptions.map((option) => (
          <StyledButton
            key={option.value}
            selected={timeFrame === option.value}
            onClick={() => handleChange(option.value)}
            aria-pressed={timeFrame === option.value}
            tabIndex={-1}
          >
            <Radio
              id={`${baseId}-${option.value}`}
              value={option.value}
              checked={timeFrame === option.value}
              onChange={() => handleChange(option.value)}
            />
            <label htmlFor={`${baseId}-${option.value}`}>{option.label}</label>
          </StyledButton>
        ))}
      </StyledButtonGroup>
      <StyledResetButton>
        <CircleOfArrowsIcon />
        <Link
          href=""
          role="button"
          onClick={(e) => {
            e.preventDefault()
            resetSimulation()
          }}
        >
          {t("reset")}
        </Link>
      </StyledResetButton>
    </StyledContainer>
  )
}

export default TimeFrameSelector
